import { UzTabs } from '@utazzo/ui';
import { BedDouble, Plane } from 'lucide-react';
import React from 'react';
import { useSearchParams } from 'react-router-dom';

import { BookingsList, type BookingsListVariant } from './BookingsList';

type Tab = 'all' | 'flights' | 'hotels';
const TABS: readonly Tab[] = ['all', 'flights', 'hotels'];

/**
 * All / Flights / Hotels over one list variant, with the tab in the URL so
 * a refresh or the detail page's back link lands on the same one. "All" is
 * first and default: the pages this serves (Action needed, Drop-offs) are
 * worked as one queue, and the product split is a filter rather than a
 * destination.
 */
export function ProductTabs({
  variant,
  gridIdPrefix,
  allCount,
}: {
  variant: BookingsListVariant;
  gridIdPrefix: string;
  /** Shown on the All tab, when the page has a count worth showing. */
  allCount?: number;
}): React.JSX.Element {
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab: Tab = TABS.includes(raw as Tab) ? (raw as Tab) : 'all';

  const setTab = (next: Tab): void => {
    // Each tab keeps its own grid state (page, filters, search) under its own
    // gridId; the tab itself is the only shared URL key.
    const nextParams = new URLSearchParams();
    nextParams.set('tab', next);
    setParams(nextParams, { replace: true });
  };

  return (
    <UzTabs
      value={tab}
      onValueChange={(v) => setTab(v as Tab)}
      listClassName="max-w-full justify-start overflow-x-auto [scrollbar-width:none] max-sm:w-full [&::-webkit-scrollbar]:hidden"
      tabs={[
        {
          value: 'all',
          label: 'All',
          ...(allCount !== undefined ? { count: allCount } : {}),
          content: <BookingsList key="all" gridId={`${gridIdPrefix}-all`} variant={variant} />,
        },
        {
          value: 'flights',
          label: 'Flights',
          icon: <Plane className="h-3.5 w-3.5" />,
          content: (
            <BookingsList
              key="flights"
              gridId={`${gridIdPrefix}-flights`}
              variant={variant}
              product="FLIGHT"
            />
          ),
        },
        {
          value: 'hotels',
          label: 'Hotels',
          icon: <BedDouble className="h-3.5 w-3.5" />,
          content: (
            <BookingsList
              key="hotels"
              gridId={`${gridIdPrefix}-hotels`}
              variant={variant}
              product="HOTEL"
            />
          ),
        },
      ]}
    />
  );
}
